import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { GridReadyEvent, RowDataUpdatedEvent, ValueFormatterParams } from 'ag-grid-community';
import { ColDef, ColumnApi, GridApi, GridOptions } from 'ag-grid-enterprise';
import { ToastrManager } from '@niceltd/cxone-components/toastr';
import { CxoneHighlightSearchCellRendererComponent } from 'src/app/reusables/ag-grid-components/cxone-highlight-search-cell-renderer/cxone-highlight-search-cell-renderer.component';
import { AgGridHelperService } from 'src/app/reusables/services/ag-grid-helper.service';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { CustomNoRowsOverlayComponent } from '../../shared/custom-no-rows-overlay/custom-no-rows-overlay.component';
import { ETLDataFeedObj } from '../cxone-etl-data-feed.model';
import { CxoneEtlDataFeedService } from '../cxone-etl-data-feed.service';
import { DataRows, ETLDataFeedViewObj, FieldDefinition } from './cxone-etl-data-feed-view.model';

@Component({
    selector: 'cv-cxone-etl-data-feed-view',
    templateUrl: './cxone-etl-data-feed-view.component.html',
    styleUrls: ['./cxone-etl-data-feed-view.component.scss']
})
export class CxoneEtlDataFeedViewComponent implements OnInit {
    @Input() selectedDataFeed: ETLDataFeedObj;
    @Output() backAction: EventEmitter<void> = new EventEmitter();
    public dataFeedViewGridHeaders: Array<ColDef>;
    public filteredDataFeedViewGridData: DataRows[];
    public dataFeedViewLabel: string;
    public pageHeader: string;
    public viewData: ETLDataFeedViewObj;
    public gridApi: GridApi;
    public gridColumnApi: ColumnApi;
    public searchInput = '';
    public searchPlaceholderText = "";
    public dataFeedViewGridOptions: GridOptions = {
        context: this,
        rowHeight: 36,
        headerHeight: 44,
        animateRows: true,
        scrollbarWidth: 8,
        onGridReady: (event: GridReadyEvent) => this.onGridReady(event),
        onRowDataUpdated: (event: RowDataUpdatedEvent) => this.setRowCountLabel(),
        onFilterChanged: () => {
            this.gridApi.redrawRows();
            this.setRowCountLabel();
        },
        components: {
            CustomNoRowsOverlayComponent,
            cxoneHighlightSearchRenderer: CxoneHighlightSearchCellRendererComponent
        },
        noRowsOverlayComponent: 'CustomNoRowsOverlayComponent',
        noRowsOverlayComponentParams: {
            noRowsOverlayTextFunc: () => this.searchInput !== '' ?
                this.getLocalizationString("No_Result_Found", "No result found")
                : this.getLocalizationString("No_Records_Found", "No records found"),
            noRowsOverlayIconPathFunc: () => {
                const path = this.searchInput !== '' ?
                    '../App_Themes/CXone/icons/no-results.svg#no-results'
                    : '../App_Themes/CXone/icons/empty-state.svg#empty-state';
                return path;
            }
        },
        isExternalFilterPresent: () => true,
        doesExternalFilterPass: (node) => {
            const result = this.agGridHelperService.doesExternalFilterPassIncludeHidden(node, this.searchInput);
            return result;
        }
    };
    public showSpinner: boolean;

    constructor(
        private agGridHelperService: AgGridHelperService,
        private cvLocalizationService: CVLocalizationService,
        private cxoneEtlDataFeedService: CxoneEtlDataFeedService,
        private toastrManager: ToastrManager) {
    }

    async ngOnInit(): Promise<void> {
        this.showSpinner = true;
        await this.loadLocalization();
        try {
            this.viewData = await this.cxoneEtlDataFeedService.loadDataFeedView(this.selectedDataFeed?._id);
            this.filteredDataFeedViewGridData = this.viewData?.SimulatedData?.DataRows;
            this.setColumnDefination(this.viewData?.SimulatedData?.FieldDefinitions);
            this.pageHeader = this.viewData?.DefinitionName;
        }
        catch (error) {
            console.log(error);
            this.toastrManager
                .showToaster('error',
                    this.getLocalizationString('Internal_Server_Error', 'Internal server error. Try again.'));
        }
        finally {
            this.gridApi.setRowData(this.filteredDataFeedViewGridData);
            this.showSpinner = false;
        }
    }

    public async loadLocalization() {
        try {
            await this.cvLocalizationService.GetSection('ETL_Data_Feed_View');
        }
        catch {
            console.log('Failed to load localization for ETL_Data_Feed_View');
        }
        finally {
            this.searchPlaceholderText = this.getLocalizationString("Search", "Search");
        }
    }

    public getLocalizationString(key: string, defaultValue: string): string {
        const baseString = this.cvLocalizationService.getCachedText("ETL_Data_Feed_View", key, defaultValue);
        return baseString;
    }

    setColumnDefination(viewColMapData: FieldDefinition[]) {
        if (!viewColMapData) return;
        this.dataFeedViewGridHeaders = viewColMapData.map((column) => ({
            headerName: column.FieldName,
            field: column.FieldName,
            colId: column.FieldName,
            cellRenderer: 'cxoneHighlightSearchRenderer',
            sortable: true,
            suppressSizeToFit: true,
            resizable: true,
            valueFormatter: (params: ValueFormatterParams) => {
                if (params.value != null)
                    return params.value.toString();
                else
                    return "";
            }
        }));
    }

    onGridReady(event: GridReadyEvent) {
        this.gridApi = event.api;
        this.gridApi.sizeColumnsToFit();
    }

    onSearchCriteriaChange = (searchCriteria: string) => {
        this.searchInput = searchCriteria;
        this.gridApi.onFilterChanged();
        if (this.gridApi?.getDisplayedRowCount() === 0)
            this.gridApi.showNoRowsOverlay();
        else
            this.gridApi?.hideOverlay();
    };

    public getTotalRecordsText(count: number): string {
        const key: string = count > 1 ? 'Total_Records_Plural' : 'Total_Records_Singular';
        const defaultValue: string = count > 1 ? '{0} records' : '{0} record';
        return this.getLocalizationString(key, defaultValue);
    }

    public setRowCountLabel() {
        const count = this.gridApi.getDisplayedRowCount();
        this.dataFeedViewLabel = this.cvLocalizationService.Format(this.getTotalRecordsText(count), count);
    }

    public getFilterText(): string {
        return this.searchInput.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
    }

    onBack() {
        this.backAction.emit(null);
    }
}
